import { useTranslation } from "react-i18next";
import { LocalizedLink, useCurrentLang } from "@/i18n/language";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BausteineSelection from "./components/BausteineSelection";
import BaliHolidayNote from "@/components/feature/BaliHolidayNote";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useTourBlockedDates } from "@/hooks/useTourBlockedDates";

const heroImg = img("your-stay/header-your-stay-01.jpg");

export default function BausteinePage() {
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const isDe = lang === "de";
  const { priceMap } = usePriceMap();
  const blockedTourDates = useTourBlockedDates();

  // Werte, die im Backstage unter „Preise → Ermäßigung" gepflegt werden.
  const discountPercent = Math.round(priceMap["discount_percent"] || 5);
  const discountNights = Math.round(priceMap["discount_nights"] || 5);
  const discountExcursions = Math.round(priceMap["discount_excursions"] || 3);

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <div className="relative h-[520px] md:h-[640px] w-full">
            <img
              src={heroImg}
              alt="Ihre Bali-Reise als Baustein planen – Villa Kompiang"
              title="Ihre Bali-Reise als Baustein planen – Villa Kompiang"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/15"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20"></div>

            <div className="absolute inset-0 z-10 flex items-center">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
                <div className="max-w-2xl">
                  <div className="mb-6 eyebrow-brand text-xs md:text-sm font-medium tracking-[0.45em] uppercase">
                    — {t("bausteine_eyebrow")} —
                  </div>
                  <h1 className="font-heading text-background-50 text-3xl md:text-5xl lg:text-6xl font-semibold leading-[1.08] tracking-tight mb-7">
                    {t("bausteine_title")}
                  </h1>
                  <p className="text-background-100 text-lg md:text-xl font-light leading-relaxed max-w-xl">
                    {t("bausteine_intro")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Agency & advantage */}
        <section className="pt-14 md:pt-20 pb-6 md:pb-8">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="mb-4 eyebrow-brand text-xs md:text-sm font-medium tracking-[0.35em] uppercase">
              {t("bausteine_agency_eyebrow")}
            </div>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 font-medium leading-[1.15] tracking-tight mb-6">
              {t("bausteine_agency_title")}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-10">
              {t("bausteine_agency_desc")}
            </p>

            <div className="border-l-4 border-primary-500 pl-6 py-1">
              <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-medium mb-3">
                {t("bausteine_advantage_title")}
              </h3>
              <p className="text-foreground-600 text-base leading-relaxed">
                {t("bausteine_advantage_desc")}
              </p>
            </div>

            {/* Unser Tipp */}
            <div className="mt-8 bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-lightbulb-flash-line text-xl"></i>
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium">
                  {t("bausteine_tip_eyebrow")}
                </h3>
              </div>
              <div className="space-y-6">
                <div>
                  <h4 className="font-heading text-base md:text-lg text-foreground-900 font-medium mb-1">
                    {t("bausteine_tip_1_title")}
                  </h4>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {t("bausteine_tip_1_desc")}
                  </p>
                </div>
                <div>
                  <h4 className="font-heading text-base md:text-lg text-foreground-900 font-medium mb-1">
                    {t("bausteine_tip_2_title")}
                  </h4>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {t("bausteine_tip_2_desc")}
                  </p>
                </div>
                <div>
                  <h4 className="inline-flex items-center bg-accent-50 border border-accent-100 text-accent-900 rounded-md px-3 py-1.5 font-heading text-base md:text-lg font-medium mb-3">
                    {t("bausteine_tip_3_title", { percent: discountPercent })}
                  </h4>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {t("bausteine_tip_3_desc", {
                      percent: discountPercent,
                      nights: discountNights,
                      excursions: discountExcursions,
                    })}
                  </p>
                </div>
                <div>
                  <h4 className="font-heading text-base md:text-lg text-foreground-900 font-medium mb-1">
                    {t("bausteine_tip_4_title")}
                  </h4>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {t("bausteine_tip_4_desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Configurator */}
        <section className="pt-0 pb-14 md:pb-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-3 md:mb-4">
              <div className="mb-4 eyebrow-brand text-xs md:text-sm font-medium tracking-[0.35em] uppercase">
                — {t("baustein_booking_eyebrow")} —
              </div>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 font-medium leading-[1.15] tracking-tight mb-4">
                {t("baustein_booking_title")}
              </h2>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {t("baustein_booking_sub")}
              </p>
            </div>

            <BaliHolidayNote
              isDe={isDe}
              blockedDates={blockedTourDates}
              className="mt-0 mb-6 md:mb-8 max-w-3xl mx-auto"
            />

            <BausteineSelection />
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 font-medium leading-[1.15] tracking-tight mb-5">
              {t("bausteine_cta_title")}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8">
              {t("bausteine_cta_desc")}
            </p>
            <LocalizedLink
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-md font-semibold uppercase tracking-[0.2em] whitespace-nowrap cursor-pointer transition-colors text-sm"
            >
              {t("bausteine_cta_button")}
              <i className="ri-arrow-right-line"></i>
            </LocalizedLink>

            <p className="mt-8 text-foreground-600 text-sm italic">
              {t("bausteine_closing")}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}